import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  Bot,
  Zap,
  Puzzle,
  ListTodo,
  Calendar,
  Building2,
  Briefcase,
  Cpu,
} from "lucide-react";
import { agentsApi } from "@/api/agents";
import { skillsApi } from "@/api/skills";
import { mcpApi } from "@/api/mcp";
import { agentTasksApi } from "@/api/agent-tasks";
import { queryKeys } from "@/lib/query-keys";
import { useTenant } from "@/context/tenant-context";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * Right-side drawer opened by double-clicking an agent node on the org
 * chart. Shows the extended agent profile at a glance — role, identity,
 * assigned skills, MCPs, and scheduled tasks — without navigating away
 * from the canvas. Avatar is clickable to open a larger lightbox.
 *
 * Structure mirrors the existing TaskDetailDrawer — fixed right-hand
 * backdrop + slide-in panel, scrollable body, close-on-Escape. Data is
 * fetched lazily only when the drawer is open.
 */

interface AgentSkillRow {
  id: string;
  tenantSkillId: string;
  enabled: boolean;
}
interface TenantSkillRow {
  id: string;
  name: string;
}

export function AgentSidecar({
  agentId,
  onClose,
}: {
  agentId: string;
  onClose: () => void;
}) {
  const { tenantId } = useTenant();
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Escape closes drawer (but only when the lightbox isn't on top — that
  // one handles its own Escape so the user can dismiss it independently).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !lightboxOpen) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, lightboxOpen]);

  const { data: agent, isLoading: agentLoading } = useQuery({
    queryKey: queryKeys.agents.detail(agentId),
    queryFn: () => agentsApi.get(agentId),
  });

  const { data: agentSkills = [], isLoading: skillsLoading } = useQuery({
    queryKey: queryKeys.agentSkills.byAgent(agentId),
    queryFn: () => skillsApi.listAgentSkills(agentId) as Promise<AgentSkillRow[]>,
  });

  // We need tenant-skills to resolve the human-readable name from the
  // agent_skills.tenantSkillId join column. Kept behind the same
  // already-cached query key the rest of the app uses.
  const { data: tenantSkills = [] } = useQuery({
    queryKey: queryKeys.tenantSkills.byCompany(tenantId),
    queryFn: () => skillsApi.listTenantSkills(tenantId) as Promise<TenantSkillRow[]>,
    enabled: !!tenantId,
  });
  const tenantSkillMap = new Map(tenantSkills.map((s) => [s.id, s]));

  const { data: agentMcps = [], isLoading: mcpsLoading } = useQuery({
    queryKey: queryKeys.agentMcps.byAgent(agentId),
    queryFn: () => mcpApi.listAgentMcps(agentId),
  });

  const { data: tenantMcps = [] } = useQuery({
    queryKey: queryKeys.tenantMcps.byCompany(tenantId),
    queryFn: () => mcpApi.listTenantMcps(tenantId),
    enabled: !!tenantId,
  });
  const tenantMcpMap = new Map(tenantMcps.map((m) => [m.id, m]));

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: queryKeys.agentTasks.byAgent(agentId),
    queryFn: () => agentTasksApi.list(tenantId, { agentId }),
    enabled: !!tenantId,
  });

  const statusDot = (status: string | undefined) => {
    if (status === "active" || status === "running") return "bg-green-500";
    if (status === "error" || status === "terminated") return "bg-red-500";
    if (status === "paused" || status === "pending_approval") return "bg-amber-500";
    return "bg-gray-400";
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 animate-in fade-in"
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer */}
      <aside
        className="fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-full flex-col overflow-hidden border-l border-border bg-surface shadow-2xl animate-in slide-in-from-right"
        role="dialog"
        aria-label={agent ? `Details for ${agent.name}` : "Agent details"}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => agent?.avatarUrl && setLightboxOpen(true)}
              disabled={!agent?.avatarUrl}
              className={cn(
                "group relative h-12 w-12 shrink-0 overflow-hidden rounded-full ring-2 ring-border transition-all",
                agent?.avatarUrl && "hover:ring-accent",
              )}
              aria-label={agent?.avatarUrl ? "View larger avatar" : "No avatar"}
              title={agent?.avatarUrl ? "Click to view larger" : undefined}
            >
              {agent?.avatarUrl ? (
                <img
                  src={agent.avatarUrl}
                  alt={agent.name}
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-surface-hover text-text-muted">
                  <Bot size={22} />
                </div>
              )}
            </button>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-text-primary">
                {agent?.name ?? (agentLoading ? "Loading…" : "Unknown agent")}
              </h2>
              <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-muted">
                <span
                  className={cn("inline-block h-1.5 w-1.5 rounded-full", statusDot(agent?.status))}
                />
                <span className="capitalize">{agent?.status ?? "—"}</span>
                {agent?.adapterType && (
                  <>
                    <span>·</span>
                    <span className="capitalize">{agent.adapterType.replace("_", " ")}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Identity */}
          <Section icon={Briefcase} title="Role & Identity">
            <KV label="Role / Job Title" value={agent?.title ?? undefined} />
            <KV label="Seniority" value={agent?.role} capitalize />
            <KV label="Department" value={agent?.department ?? undefined} />
            <KV label="Reports to" value={agent?.reportsTo ? "Yes" : "—"} hide={!agent?.reportsTo} />
          </Section>

          {/* Model */}
          {agent && (
            <Section icon={Cpu} title="Runtime">
              <KV
                label="Adapter"
                value={agent.adapterType?.replace("_", " ")}
                capitalize
              />
              <KV
                label="Model"
                value={(agent.adapterConfig?.model as string | undefined) ?? undefined}
                mono
              />
              <KV label="Runtime tier" value={agent.runtimeTier} capitalize />
            </Section>
          )}

          {/* Skills */}
          <Section icon={Zap} title={`Skills (${agentSkills.length})`}>
            {skillsLoading ? (
              <EmptyLine>Loading…</EmptyLine>
            ) : agentSkills.length === 0 ? (
              <EmptyLine>No skills assigned.</EmptyLine>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {agentSkills.map((row) => {
                  const name = tenantSkillMap.get(row.tenantSkillId)?.name ?? row.tenantSkillId;
                  return (
                    <li key={row.id}>
                      <Badge variant={row.enabled ? "default" : "secondary"}>
                        {name}
                        {!row.enabled && " (disabled)"}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          {/* MCPs */}
          <Section icon={Puzzle} title={`MCPs (${agentMcps.length})`}>
            {mcpsLoading ? (
              <EmptyLine>Loading…</EmptyLine>
            ) : agentMcps.length === 0 ? (
              <EmptyLine>No MCPs assigned.</EmptyLine>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {agentMcps.map((row) => {
                  const tm = tenantMcpMap.get(row.tenantMcpId);
                  const name = tm?.name ?? row.tenantMcpId;
                  return (
                    <li key={row.id}>
                      <Badge variant={row.enabled ? "default" : "secondary"}>
                        {name}
                        {!row.enabled && " (disabled)"}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          {/* Scheduled Tasks */}
          <Section icon={ListTodo} title={`Scheduled Tasks (${tasks.length})`}>
            {tasksLoading ? (
              <EmptyLine>Loading…</EmptyLine>
            ) : tasks.length === 0 ? (
              <EmptyLine>No tasks configured.</EmptyLine>
            ) : (
              <ul className="space-y-2">
                {tasks.map((task) => (
                  <li
                    key={task.id}
                    className="rounded-md border border-border bg-background p-2.5"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="truncate text-sm font-medium text-text-primary">
                        {task.title}
                      </p>
                      <Badge
                        variant={task.enabled ? "success" : "secondary"}
                        className="shrink-0"
                      >
                        {task.enabled ? task.type : "disabled"}
                      </Badge>
                    </div>
                    {task.cronExpression && (
                      <p className="mt-1 font-mono text-[11px] text-text-muted">
                        {task.cronExpression} · {task.timezone}
                      </p>
                    )}
                    {task.nextRunAt && (
                      <p className="mt-0.5 flex items-center gap-1 text-[11px] text-text-muted">
                        <Calendar size={10} />
                        Next: {new Date(task.nextRunAt).toLocaleString()}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Housekeeping */}
          {agent && (
            <Section icon={Building2} title="Meta">
              <KV label="Created" value={fmt(agent.createdAt)} />
              <KV label="Updated" value={fmt(agent.updatedAt)} />
              {agent.lastHeartbeatAt && (
                <KV label="Last heartbeat" value={fmt(agent.lastHeartbeatAt)} />
              )}
            </Section>
          )}
        </div>
      </aside>

      {lightboxOpen && agent?.avatarUrl && (
        <AvatarLightbox
          src={agent.avatarUrl}
          alt={agent.name}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Tiny helpers kept local to avoid adding new generic primitives.

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Bot;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-1.5">
        <Icon size={13} className="text-text-muted" />
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

function KV({
  label,
  value,
  capitalize,
  mono,
  hide,
}: {
  label: string;
  value: string | null | undefined;
  capitalize?: boolean;
  mono?: boolean;
  hide?: boolean;
}) {
  if (hide) return null;
  return (
    <div className="flex items-baseline gap-3 py-0.5 text-sm">
      <span className="w-28 shrink-0 text-text-muted">{label}</span>
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-text-primary",
          !value && "italic text-text-muted",
          capitalize && value && "capitalize",
          mono && "font-mono text-xs",
        )}
        title={value ?? undefined}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return <p className="text-sm italic text-text-muted">{children}</p>;
}

function fmt(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ───────────────────────────────────────────────────────────────────────
// Avatar lightbox — simple centred image modal. Click anywhere (including
// the image) to dismiss. Escape to close. No library — keeps the bundle
// unchanged.

function AvatarLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6 animate-in fade-in"
      onClick={onClose}
      role="dialog"
      aria-label="Avatar preview"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-md p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        aria-label="Close preview"
      >
        <X size={22} />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-[85vh] max-w-[85vw] rounded-2xl object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
